type DateRuleBounds = {
  min?: string;
  max?: string;
};

type DateRuleContext = {
  formData?: unknown;
  stepId?: string;
  parentPath?: string;
  contextData?: Record<string, unknown>;
};

type DateRuleFieldShape = {
  key?: string;
  label?: string;
  min?: string | number;
  max?: string | number;
  validation?: {
    minDaysFromNow?: unknown;
    minDaysFromNowMessage?: string;
    onlyToday?: boolean;
    onlyTodayMessage?: string;
    beforeField?:
      | string
      | {
          field: string;
          message?: string;
          inclusive?: boolean;
        };
    required?: boolean;
  };
};

const DATE_ONLY_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const REVERSED_DATE_RE = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;

const pad2 = (value: number | string): string => String(value).padStart(2, "0");

export const getLocalDateString = (daysFromNow = 0): string => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + daysFromNow);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )}`;
};

export const normalizeDateOnly = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === "") return undefined;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
      value.getDate(),
    )}`;
  }

  const raw = String(value).trim();
  const dateOnly = raw.includes("T") ? raw.split("T")[0] : raw;

  const iso = DATE_ONLY_RE.exec(dateOnly);
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`;

  const reversed = REVERSED_DATE_RE.exec(dateOnly);
  if (reversed) return `${reversed[3]}-${pad2(reversed[2])}-${pad2(reversed[1])}`;

  return undefined;
};

const shiftDate = (dateValue: string, days: number): string | undefined => {
  const normalized = normalizeDateOnly(dateValue);
  if (!normalized) return undefined;

  const [year, month, day] = normalized.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )}`;
};

const laterDate = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

const earlierDate = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
};

const getByPath = (source: unknown, path?: string): unknown => {
  if (!source || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) {
    return (source as Record<string, unknown>)[path];
  }
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .reduce(
      (accumulator: unknown, key) =>
        accumulator != null
          ? (accumulator as Record<string, unknown>)[key]
          : undefined,
      source,
    );
};

const resolveFieldValue = (
  fieldPath: string | undefined,
  {
    formData,
    stepId,
    parentPath,
    contextData,
  }: DateRuleContext,
): unknown => {
  const path = String(fieldPath || "").trim();
  if (!path) return undefined;

  const contextValue = getByPath(contextData, path);
  if (contextValue !== undefined) return contextValue;

  const parentValue = parentPath
    ? getByPath(formData, `${parentPath}.${path}`)
    : undefined;
  if (parentValue !== undefined) return parentValue;

  const directValue = getByPath(formData, path);
  if (directValue !== undefined) return directValue;

  const stepValue = stepId ? getByPath(formData, `${stepId}.${path}`) : undefined;
  if (stepValue !== undefined) return stepValue;

  const scopes =
    formData && typeof formData === "object" ? Object.keys(formData) : [];
  for (const scope of scopes) {
    const scopedValue = getByPath(formData, `${scope}.${path}`);
    if (scopedValue !== undefined) return scopedValue;
  }

  return undefined;
};

const getBeforeFieldConfig = (validation: unknown) => {
  const beforeField = (validation as DateRuleFieldShape["validation"])?.beforeField;
  if (!beforeField) return undefined;

  if (typeof beforeField === "string") {
    return { field: beforeField, inclusive: true };
  }

  return {
    field: beforeField.field,
    message: beforeField.message,
    inclusive: beforeField.inclusive !== false,
  };
};

export const resolveDateFieldBounds = (
  field: unknown,
  context: DateRuleContext = {},
): DateRuleBounds => {
  const fieldShape = field as DateRuleFieldShape | undefined;
  const validation = fieldShape?.validation || {};
  let min = normalizeDateOnly(fieldShape?.min);
  let max = normalizeDateOnly(fieldShape?.max);

  if (validation.minDaysFromNow !== undefined) {
    min = laterDate(min, getLocalDateString(Number(validation.minDaysFromNow) || 0));
  }

  if (validation.onlyToday) {
    const today = getLocalDateString();
    min = today;
    max = today;
  }

  const beforeField = getBeforeFieldConfig(validation);
  const beforeFieldDate = normalizeDateOnly(
    resolveFieldValue(beforeField?.field, context),
  );

  if (beforeFieldDate) {
    const limit = beforeField?.inclusive
      ? beforeFieldDate
      : shiftDate(beforeFieldDate, -1);
    max = earlierDate(max, limit);
  }

  return { min, max };
};

export const getDateFieldRuleError = (
  field: unknown,
  value: unknown,
  context: DateRuleContext = {},
): string | undefined => {
  const dateValue = normalizeDateOnly(value);
  if (!dateValue) return undefined;

  const fieldShape = field as DateRuleFieldShape | undefined;
  const validation = fieldShape?.validation || {};
  const label = String(fieldShape?.label || fieldShape?.key || "Date");

  if (validation.onlyToday && dateValue !== getLocalDateString()) {
    return validation.onlyTodayMessage || `${label} must be today`;
  }

  if (validation.minDaysFromNow !== undefined) {
    const minDate = getLocalDateString(Number(validation.minDaysFromNow) || 0);
    if (dateValue < minDate) {
      return validation.minDaysFromNowMessage || `${label} must be on or after ${minDate}`;
    }
  }

  const fieldMin = normalizeDateOnly(fieldShape?.min);
  if (fieldMin && dateValue < fieldMin) {
    return `${label} must be on or after ${fieldMin}`;
  }

  const beforeField = getBeforeFieldConfig(validation);
  const beforeFieldDate = normalizeDateOnly(
    resolveFieldValue(beforeField?.field, context),
  );

  if (beforeFieldDate) {
    const isInvalid = beforeField?.inclusive
      ? dateValue > beforeFieldDate
      : dateValue >= beforeFieldDate;
    if (isInvalid) {
      return (
        beforeField?.message ||
        `${label} must be ${beforeField?.inclusive ? "on or before" : "before"} ${beforeFieldDate}`
      );
    }
  }

  const fieldMax = normalizeDateOnly(fieldShape?.max);
  if (fieldMax && dateValue > fieldMax) {
    return `${label} must be on or before ${fieldMax}`;
  }

  return undefined;
};
